import {
  NEEDS_YOU_FEED_SECTION,
  PINNED_FEED_GROUP,
  SNOOZED_FEED_GROUP,
  UPDATES_FEED_SECTION,
  WORKING_FEED_GROUP,
} from '@littlebird/sdk';

import type { FeedItem } from './feed-search';
import type { ReactNode } from 'react';

export type FeedGroupLead = { count: number; rows: ReactNode };

export type FeedGroupLeads = Readonly<Partial<Record<string, FeedGroupLead>>>;

// A section's heading (level 0), or the heading of a group inside a section (level 1), such as an app's
export type FeedGroup = {
  id: string;
  label: string;
  count: number;
  collapsed: boolean;
  level: 0 | 1;
  // The app or person a level 1 group is about, for its icon
  key?: string;
};

// The chevron under a section that shows only its first cards, or that the user opened to all of them
export type FeedSectionMore = {
  sectionId: string;
  hidden: number;
  expanded: boolean;
};

export type FeedGroupLayout = {
  items: FeedItem[];
  headingsBefore: FeedGroup[][];
  // The chevron after an item, when it is the last one its section shows
  moreAfter: (FeedSectionMore | undefined)[];
  trailingHeadings: FeedGroup[];
};

// A section shows this many cards until the user asks for the rest
export const FEED_SECTION_PREVIEW = 5;

type FeedLayoutInput = {
  items: readonly FeedItem[];
  // Set on the first item of each section
  groupIds: readonly (string | undefined)[];
  groupLabels: readonly (string | undefined)[];
  // Set on the first item of each group inside a section
  subgroupIds?: readonly (string | undefined)[];
  subgroupLabels?: readonly (string | undefined)[];
  // A section folded to its heading
  isCollapsed: (id: string) => boolean;
  // A section the user opened past its first cards
  isExpanded?: (id: string) => boolean;
  // Sections the host leads with rows of its own, shown even when no card is in them
  leadGroupIds?: readonly string[];
  // The same for every section, or set per section, such as one whose host rows take some of its places
  previewCount?: number | ((sectionId: string) => number);
};

type Section = {
  id: string | undefined;
  label: string;
  items: { item: FeedItem; subgroup?: { id: string; label: string } }[];
};

// Where a section the host leads goes when no card put it in the list
const placeLeadSection = (sections: Section[], id: string) => {
  const section: Section = { id, label: id, items: [] };
  if (id === WORKING_FEED_GROUP) {
    const pinnedIndex = sections.findIndex(
      (candidate) => candidate.id === PINNED_FEED_GROUP
    );
    sections.splice(pinnedIndex < 0 ? 0 : pinnedIndex + 1, 0, section);
    return;
  }
  const before = new Set(
    id === NEEDS_YOU_FEED_SECTION
      ? [UPDATES_FEED_SECTION, SNOOZED_FEED_GROUP]
      : [SNOOZED_FEED_GROUP]
  );
  const index = sections.findIndex(
    (candidate) => candidate.id !== undefined && before.has(candidate.id)
  );
  sections.splice(index < 0 ? sections.length : index, 0, section);
};

export const layoutFeedGroups = ({
  items,
  groupIds,
  groupLabels,
  subgroupIds = [],
  subgroupLabels = [],
  isCollapsed,
  isExpanded = () => false,
  leadGroupIds = [],
  previewCount = FEED_SECTION_PREVIEW,
}: FeedLayoutInput): FeedGroupLayout => {
  const layout: FeedGroupLayout = {
    items: [],
    headingsBefore: [],
    moreAfter: [],
    trailingHeadings: [],
  };
  const sections: Section[] = [];
  items.forEach((item, index) => {
    const id = groupIds[index];
    if (id !== undefined || sections.length === 0) {
      sections.push({
        id,
        label: id === undefined ? '' : (groupLabels[index] ?? id),
        items: [],
      });
    }
    const subgroupId = subgroupIds[index];
    sections[sections.length - 1]?.items.push({
      item,
      subgroup:
        subgroupId === undefined
          ? undefined
          : { id: subgroupId, label: subgroupLabels[index] ?? subgroupId },
    });
  });

  for (const id of leadGroupIds) {
    if (!sections.some((section) => section.id === id))
      placeLeadSection(sections, id);
  }

  let pending: FeedGroup[] = [];
  for (const section of sections) {
    const sectionId = section.id;
    const collapsed = sectionId !== undefined && isCollapsed(sectionId);
    if (sectionId !== undefined)
      pending.push({
        id: sectionId,
        label: section.label,
        count: section.items.length,
        collapsed,
        level: 0,
      });
    if (collapsed) continue;

    const expanded = sectionId !== undefined && isExpanded(sectionId);
    const preview =
      typeof previewCount === 'number'
        ? previewCount
        : sectionId === undefined
          ? Infinity
          : previewCount(sectionId);
    const capped = sectionId !== undefined && section.items.length > preview;
    const visible =
      capped && !expanded ? section.items.slice(0, preview) : section.items;

    // A group inside the section counts all its cards, shown or not
    const subgroupCounts = new Map<string, number>();
    let current: string | undefined;
    for (const { subgroup } of section.items) {
      if (subgroup) current = subgroup.id;
      if (current !== undefined)
        subgroupCounts.set(current, (subgroupCounts.get(current) ?? 0) + 1);
    }

    visible.forEach(({ item, subgroup }, index) => {
      if (subgroup && sectionId !== undefined)
        pending.push({
          id: `${sectionId}/${subgroup.id}`,
          label: subgroup.label,
          count: subgroupCounts.get(subgroup.id) ?? 0,
          collapsed: false,
          level: 1,
          key: subgroup.id,
        });
      layout.items.push(item);
      layout.headingsBefore.push(pending);
      pending = [];
      layout.moreAfter.push(
        capped && sectionId !== undefined && index === visible.length - 1
          ? {
              sectionId,
              hidden: section.items.length - preview,
              expanded,
            }
          : undefined
      );
    });
  }
  layout.trailingHeadings = pending;

  return layout;
};
